import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Info, Clock, MapPin, Euro, AlertCircle } from 'lucide-react';

/**
 * Direct Answer Block - Optimized for AEO (Answer Engine Optimization)
 * This appears in Google AI Overviews and voice assistants
 */
export const DirectAnswer = ({ answer, question, source = "VersaillesPlus" }) => {
  if (!answer) return null;

  return (
    <>
      {/* Speakable schema for voice assistants */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": [".direct-answer"]
            }
          })}
        </script>
      </Helmet>
      
      <div 
        className="direct-answer bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-500 p-5 rounded-r-lg mb-6 shadow-sm"
        itemScope 
        itemType="https://schema.org/Answer"
      >
        <div className="flex items-center gap-2 text-amber-700 text-sm font-semibold mb-2">
          <CheckCircle className="w-4 h-4" />
          <span>Réponse directe</span>
        </div>
        {question && (
          <p className="text-sm text-stone-500 mb-2 italic">"{question}"</p>
        )}
        <p 
          className="text-stone-800 text-lg leading-relaxed"
          itemProp="text"
        >
          {answer}
        </p>
        <cite className="block text-xs text-stone-400 mt-3">
          Source : {source}
        </cite>
      </div>
    </>
  );
};

/**
 * Key Takeaways Block - Optimized for Featured Snippets
 */
export const KeyTakeaways = ({ 
  takeaways, 
  title = "À retenir",
  icon = "📌"
}) => {
  if (!takeaways || takeaways.length === 0) return null;

  return (
    <div className="key-takeaways bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
      <h3 className="flex items-center gap-2 text-blue-900 font-bold text-lg mb-4">
        <span>{icon}</span>
        {title}
      </h3>
      <ul className="space-y-2">
        {takeaways.map((item, idx) => (
          <li 
            key={idx}
            className="flex items-start gap-3 text-blue-800"
          >
            <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Comparison Table - Optimized for Featured Snippets
 */
export const ComparisonTable = ({ 
  headers, 
  rows, 
  caption,
  className = ""
}) => {
  if (!headers || !rows) return null;

  return (
    <div className={`overflow-x-auto mb-6 ${className}`}>
      <table 
        className="comparison-table w-full border-collapse bg-white rounded-lg overflow-hidden shadow-sm"
        itemScope 
        itemType="https://schema.org/Table"
      >
        {caption && (
          <caption className="text-left text-sm text-stone-600 mb-2 font-medium">
            {caption}
          </caption>
        )}
        <thead>
          <tr className="bg-stone-800 text-white">
            {headers.map((header, idx) => (
              <th 
                key={idx}
                className="px-4 py-3 text-left text-sm font-semibold"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr 
              key={rowIdx}
              className={rowIdx % 2 === 0 ? 'bg-stone-50' : 'bg-white'}
            >
              {row.map((cell, cellIdx) => (
                <td 
                  key={cellIdx}
                  className="px-4 py-3 text-sm text-stone-700 border-b border-stone-100"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * HowTo Steps - Optimized for HowTo Schema
 */
export const HowToSteps = ({ 
  title,
  description,
  steps,
  totalTime,
  image
}) => {
  if (!steps || steps.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "description": description,
    "totalTime": totalTime || "PT4H",
    "step": steps.map((step, idx) => ({
      "@type": "HowToStep",
      "position": idx + 1,
      "name": step.name,
      "text": step.text,
      "image": step.image
    }))
  };

  if (image) schema.image = image;

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>
      
      <div className="howto-steps mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-2">{title}</h2>
        {description && (
          <p className="text-stone-600 mb-6">{description}</p>
        )}
        {totalTime && (
          <div className="flex items-center gap-2 text-stone-500 text-sm mb-6">
            <Clock className="w-4 h-4" />
            <span>Durée totale estimée : {formatDuration(totalTime)}</span>
          </div>
        )}
        
        <ol className="space-y-4">
          {steps.map((step, idx) => (
            <li 
              key={idx}
              className="flex gap-4 p-4 bg-white rounded-lg border border-stone-200 shadow-sm"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-700 text-white flex items-center justify-center font-bold text-sm">
                {idx + 1}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-stone-900 mb-1">
                  {step.name}
                </h3>
                <p className="text-stone-600 text-sm">
                  {step.text}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </>
  );
};

/**
 * Practical Info Box - For quick reference
 */
export const PracticalInfoBox = ({ 
  duration,
  price,
  location,
  tips,
  accessibility
}) => {
  return (
    <div className="practical-info bg-stone-100 rounded-xl p-5 mb-6">
      <h3 className="font-bold text-stone-900 mb-4 flex items-center gap-2">
        <Info className="w-5 h-5 text-stone-600" />
        Informations pratiques
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {duration && (
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-stone-500 mt-0.5" />
            <div>
              <span className="text-xs text-stone-500 uppercase tracking-wide">Durée</span>
              <p className="text-stone-800 font-medium">{duration}</p>
            </div>
          </div>
        )}
        
        {price && (
          <div className="flex items-start gap-3">
            <Euro className="w-5 h-5 text-stone-500 mt-0.5" />
            <div>
              <span className="text-xs text-stone-500 uppercase tracking-wide">Tarif</span>
              <p className="text-stone-800 font-medium">{price}</p>
            </div>
          </div>
        )}
        
        {location && (
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-stone-500 mt-0.5" />
            <div>
              <span className="text-xs text-stone-500 uppercase tracking-wide">Accès</span>
              <p className="text-stone-800 font-medium">{location}</p>
            </div>
          </div>
        )}
        
        {accessibility && (
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-stone-500 mt-0.5" />
            <div>
              <span className="text-xs text-stone-500 uppercase tracking-wide">Accessibilité</span>
              <p className="text-stone-800 font-medium">{accessibility}</p>
            </div>
          </div>
        )}
      </div>
      
      {tips && tips.length > 0 && (
        <div className="mt-4 pt-4 border-t border-stone-200">
          <p className="text-xs text-stone-500 uppercase tracking-wide mb-2">💡 Conseils</p>
          <ul className="text-sm text-stone-700 space-y-1">
            {tips.map((tip, idx) => (
              <li key={idx}>• {tip}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * FAQ Accordion with Schema - Optimized for PAA (People Also Ask)
 */
export const FAQAccordion = ({ faqs, title = "Questions fréquentes" }) => {
  const [openIndex, setOpenIndex] = React.useState(null);

  if (!faqs || faqs.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>
      
      <div className="faq-accordion mb-8">
        <h2 className="text-2xl font-bold text-stone-900 mb-6">{title}</h2>
        
        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div 
              key={idx}
              className="border border-stone-200 rounded-lg overflow-hidden"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full px-5 py-4 text-left bg-white hover:bg-stone-50 flex justify-between items-center transition-colors"
              >
                <span 
                  className="font-medium text-stone-900"
                  itemProp="name"
                >
                  {faq.question}
                </span>
                <span className={`transform transition-transform ${openIndex === idx ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </button>
              
              {openIndex === idx && (
                <div 
                  className="px-5 py-4 bg-stone-50 border-t border-stone-200"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p 
                    className="text-stone-700"
                    itemProp="text"
                  >
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

/**
 * Source Authority Block - E-E-A-T Signal
 */
export const SourceAuthority = ({ 
  author = "VersaillesPlus",
  lastUpdated,
  sources = []
}) => {
  return (
    <div className="source-authority bg-stone-50 rounded-lg p-4 mt-8 border border-stone-200">
      <div className="flex flex-wrap items-center gap-4 text-sm text-stone-600">
        <div className="flex items-center gap-2">
          <span className="font-medium">Auteur :</span>
          <span>{author}</span>
        </div>
        
        {lastUpdated && (
          <div className="flex items-center gap-2">
            <span className="font-medium">Mis à jour :</span>
            <time dateTime={lastUpdated}>
              {new Date(lastUpdated).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </time>
          </div>
        )}
      </div>
      
      {sources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-stone-200">
          <span className="text-xs text-stone-500 uppercase tracking-wide">Sources officielles :</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {sources.map((source, idx) => (
              <a
                key={idx}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs bg-white px-3 py-1 rounded-full border border-stone-200 hover:border-red-300 hover:text-red-700 transition-colors"
              >
                {source.name}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to format ISO duration
function formatDuration(isoDuration) {
  if (!isoDuration) return '';
  
  const match = isoDuration.match(/PT(\d+)H?(\d+)?M?/);
  if (!match) return isoDuration;
  
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  
  if (hours && minutes) return `${hours}h${minutes}min`;
  if (hours) return `${hours} heure${hours > 1 ? 's' : ''}`;
  if (minutes) return `${minutes} minutes`;
  return isoDuration;
}

export default DirectAnswer;
